import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { motion } from 'framer-motion';
import SavingsIcon from '@mui/icons-material/Savings';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RecommendIcon from '@mui/icons-material/Recommend';
import { API_BASE } from '../config';

const CostTracker = forwardRef(({ compact = false }, ref) => {
  const [costs, setCosts] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchCosts = async () => {
    try {
      const response = await fetch(`${API_BASE}/costs/session`);
      const data = await response.json();
      setCosts(data);
    } catch (err) {
      // Failed to fetch costs
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await fetch(`${API_BASE}/costs/pricing`);
      const data = await response.json();
      setPricing(data);
    } catch (err) {
      // Failed to fetch pricing
    }
  };

  const resetCosts = async () => {
    setLoading(true);
    try {
      await fetch(`${API_BASE}/costs/reset`, { method: 'POST' });
      await fetchCosts();
    } catch (err) {
      // Failed to reset costs
    } finally {
      setLoading(false);
    }
  };

  // Expose refresh method to parent via ref
  useImperativeHandle(ref, () => ({
    refresh: fetchCosts
  }));

  useEffect(() => {
    // Only fetch once on mount - no polling!
    fetchCosts();
    fetchPricing();
  }, []);

  if (!costs || !costs.total_cost) return null;

  // Compact view for agent cards
  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.75rem' }}>
        <Tooltip title={`Saved ${(costs.savings_percent || 0)}% vs GPT-4`}>
          <Chip
            icon={<SavingsIcon sx={{ fontSize: 14 }} />}
            label={`$${(costs.total_cost || 0).toFixed(4)}`}
            size="small"
            sx={{
              background: (costs.savings_percent || 0) > 50 ? '#3f9679' : '#3ad0de',
              color: 'white',
              fontSize: '0.7rem',
              height: 20
            }}
          />
        </Tooltip>
        {(costs.total_savings || 0) > 0 && (
          <Tooltip title="Total savings this session">
            <Typography variant="caption" sx={{ color: '#3f9679', fontWeight: 600 }}>
              -${(costs.total_savings || 0).toFixed(4)}
            </Typography>
          </Tooltip>
        )}
      </Box>
    );
  }

  // Full card view
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            p: 1.5,
            background: 'rgba(76, 175, 80, 0.05)',
            border: '1px solid rgba(76, 175, 80, 0.2)',
            borderRadius: 1,
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Left: Savings */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title={`Saved ${(costs.savings_percent || 0).toFixed(1)}% vs GPT-4`}>
              <Chip
                icon={<SavingsIcon sx={{ fontSize: 16 }} />}
                label={`${(costs.savings_percent || 0).toFixed(0)}% saved`}
                size="small"
                sx={{
                  background: (costs.savings_percent || 0) > 50 ? '#3f9679' : '#3ad0de',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}
              />
            </Tooltip>
            
            <Tooltip title="Actual cost this session">
              <Typography variant="body2" sx={{ color: '#3ad0de', fontWeight: 600 }}>
                ${(costs.total_cost || 0).toFixed(4)}
              </Typography>
            </Tooltip>
            
            {(costs.total_savings || 0) > 0 && (
              <Tooltip title="Total saved">
                <Typography variant="body2" sx={{ color: '#3f9679', fontWeight: 600 }}>
                  -${(costs.total_savings || 0).toFixed(4)}
              </Typography>
              </Tooltip>
            )}
          </Box>

          {/* Middle: Provider chips */}
          {costs.by_provider && Object.keys(costs.by_provider).length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {Object.entries(costs.by_provider).map(([provider, cost]) => (
                <Chip
                  key={provider}
                  label={provider}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: 20,
                    background: 'rgba(74, 163, 181, 0.2)',
                    borderColor: '#3ad0de',
                    color: '#3ad0de'
                  }}
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          {/* Right: Actions */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {costs.python_optimizations && costs.python_optimizations.cache_hits > 0 && (
              <Tooltip title={`${costs.python_optimizations.cache_hits} cache hits`}>
                <Chip
                  icon={<SavingsIcon sx={{ fontSize: 14 }} />}
                  label={costs.python_optimizations.cache_hits}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: 20,
                    background: 'rgba(76, 175, 80, 0.2)',
                    borderColor: '#3f9679',
                    color: '#3f9679'
                  }}
                  variant="outlined"
                />
              </Tooltip>
            )}
            <Tooltip title="View pricing details">
              <IconButton size="small" onClick={() => setShowDetails(true)} sx={{ width: 24, height: 24 }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh costs">
              <IconButton size="small" onClick={fetchCosts} disabled={loading} sx={{ width: 24, height: 24 }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </motion.div>

      {/* Pricing Details Dialog */}
      <Dialog
        open={showDetails}
        onClose={() => setShowDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachMoneyIcon sx={{ color: '#3ad0de' }} />
          Model Pricing & Recommendations
        </DialogTitle>
        <DialogContent>
          {pricing && (
            <>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RecommendIcon fontSize="small" color="success" /> Smart Recommendations
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                {pricing?.recommendations && (
                  <>
                    <Chip
                      label={`Cheapest: ${pricing.recommendations.cheapest}`}
                      sx={{ background: '#3f9679', color: 'white' }}
                    />
                    <Chip
                      label={`Best Value: ${pricing.recommendations.best_value}`}
                      sx={{ background: '#3ad0de', color: 'white' }}
                    />
                    <Chip
                      label={`Most Capable: ${pricing.recommendations.most_capable}`}
                      sx={{ background: '#7595ff', color: 'white' }}
                    />
                    <Chip
                      label={`Best Reasoning: ${pricing.recommendations.best_reasoning}`}
                      sx={{ background: '#3ad0de', color: 'white' }}
                    />
                    {pricing.recommendations.most_creative && (
                      <Chip
                        label={`Most Creative: ${pricing.recommendations.most_creative}`}
                        sx={{ background: '#9b59b6', color: 'white' }}
                      />
                    )}
                  </>
                )}
              </Box>

              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AttachMoneyIcon fontSize="small" color="primary" />
                Pricing Table (per 1M tokens)
              </Typography>
              <TableContainer component={Paper} sx={{ background: '#1a1a2e' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Model</TableCell>
                      <TableCell align="right">Input</TableCell>
                      <TableCell align="right">Output</TableCell>
                      <TableCell align="right">Provider</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pricing?.pricing && Object.entries(pricing.pricing).map(([model, prices]) => (
                      <TableRow key={model}>
                        <TableCell>{model}</TableCell>
                        <TableCell align="right">${prices.input.toFixed(2)}</TableCell>
                        <TableCell align="right">${prices.output.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={model.split('-')[0]}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccountTreeIcon fontSize="small" color="primary" /> Task-Based Routing
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Whisk automatically routes to the best model for each task:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {pricing?.routing && Object.entries(pricing.routing).map(([task, model]) => (
                  <Box key={task} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      {task.charAt(0).toUpperCase() + task.slice(1)} tasks:
                    </Typography>
                    <Chip label={model} size="small" variant="outlined" />
                  </Box>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default CostTracker;
